import {
  getDonationParticipantsAPI,
  getDonationParticipantsExcelAPI,
} from '@/src/apis/rest/donation';
import { GetDonationParticipantsRequestParams } from '@/src/apis/rest/donation.dtos';
import Button from '@/src/components/atom/Button';
import DateRangePicker from '@/src/components/atom/DateRangePicker';
import Dialog from '@/src/components/atom/Dialog';
import ExcelDownloadButton from '@/src/components/atom/ExcelDownloadButton';
import Input from '@/src/components/atom/Input';
import Table from '@/src/components/atom/Table';
import FormContent from '@/src/components/molecule/FormContent';
import { Form, FormField } from '@/src/components/ui/form';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { donationParticipantListColumn } from '@/src/pages/Donation/column';
import { useQuery } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { useForm } from 'react-hook-form';

interface DonationParticipantsModalProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  id: number | null;
  contentStartedAt?: string;
  contentEndedAt?: string;
}

interface FormValues
  extends Partial<
    GetDonationParticipantsRequestParams & { createdAt: DateRange }
  > {
  createdAt: {
    from: undefined;
    to: undefined;
  };
}

const DEFAULT_VALIES = {};

export default function DonationParticipantsModal({
  open: openProps,
  onOpenChange,
  id,
  contentStartedAt,
  contentEndedAt,
}: DonationParticipantsModalProps) {
  // state
  const gridRef = useRef<AgGridReact>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filterValues, setFilterValues] = useState<
    Omit<FormValues, 'createdAt'>
  >({});

  // form
  const form = useForm<FormValues>({
    defaultValues: DEFAULT_VALIES,
  });

  // query
  const {
    data: donationParticipantsData,
    refetch,
    isFetching,
    isError,
  } = useQuery({
    queryKey: ['donation-participants', id, page, pageSize],
    queryFn: () => {
      return getDonationParticipantsAPI({
        page,
        pageSize,
        donationId: Number(id),
        ...form.watch(),
      });
    },
    enabled: !!id,
  });

  // event
  const handleSubmit = (values: FormValues) => {
    setPage(1);

    const newValues = { ...values };

    setFilterValues(newValues);
  };

  const handleCloseModal = () => {
    onOpenChange(false);
  };
  const handlePageChange = (page: number, pageSize: number) => {
    setPage(page);
    setPageSize(pageSize);
  };

  // render
  const getDateDisabledMatcher = (date: Date) => {
    if (dayjs(contentStartedAt).isAfter(date, 'D')) return true;
    if (dayjs(contentEndedAt).isBefore(date, 'D')) return true;

    return false;
  };

  // effect
  useEffect(() => {
    refetch();
  }, [filterValues, refetch]);

  return (
    <>
      <Dialog
        open={openProps}
        onOpenChange={handleCloseModal}
        contentProps={{
          style: {
            width: 'calc(100vw - 24px)',
          },
          className: 'max-w-[967px]',
        }}
        title={
          <div className="flex items-center gap-2">
            참여한 회원 목록
            <ExcelDownloadButton
              disabled={
                isError || isFetching || !donationParticipantsData?.rows?.length
              }
              buttonSize="small"
              fileName="기부하기_참여_회원_리스트"
              downloadExcelAPI={(password, searchParams) =>
                getDonationParticipantsExcelAPI(password, {
                  ...searchParams,
                  donationId: Number(id),
                })
              }
              searchParams={form.watch()}
            />
          </div>
        }
      >
        <Form {...form}>
          <form
            className="mb-4 grid grid-cols-[1fr_auto_1fr_auto] gap-4"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name="createdAt"
              render={({ field, fieldState: { error } }) => (
                <FormContent
                  label="기부 참여 기간"
                  labelClassName="w-[144px] !text-b16m text-gray7 translate-y-2 font-pretendardMedium"
                  error={error}
                >
                  <DateRangePicker
                    {...field}
                    onChange={e => {
                      field.onChange(e);

                      form.setValue(
                        'createdAtFrom',
                        e?.from ? dayjs(e?.from).format('YYYY-MM-DD') : '',
                      );
                      form.setValue(
                        'createdAtTo',
                        e?.to ? dayjs(e?.to).format('YYYY-MM-DD') : '',
                      );
                    }}
                    dateDisabledMatcher={getDateDisabledMatcher}
                    defaultMonth={
                      contentStartedAt ? new Date(contentStartedAt) : undefined
                    }
                  />
                </FormContent>
              )}
            />
            <div className="h-full w-[1px] bg-gray1" />
            <FormField
              control={form.control}
              name="nickLike"
              render={({ field, fieldState: { error } }) => (
                <FormContent
                  label="닉네임"
                  labelClassName="w-[144px] !text-b16m text-gray7 translate-y-2 font-pretendardMedium"
                  error={error}
                >
                  <Input
                    {...field}
                    onClear={() => field.onChange('')}
                    placeholder="닉네임을 입력해주세요"
                    isEnterSubmit
                  />
                </FormContent>
              )}
            />
            <Button
              type="submit"
              className="flex-shrink-0"
              buttonColor="secondary"
            >
              {BUTTON_CONST.search}
            </Button>
          </form>
        </Form>
        <Table
          ref={gridRef}
          rowData={isError ? [] : donationParticipantsData?.rows || []}
          isPaginationDataMapping
          columnDefs={donationParticipantListColumn}
          total={donationParticipantsData?.count || 0}
          page={page}
          pageSize={pageSize}
          handlePaginationClick={handlePageChange}
        />
      </Dialog>
    </>
  );
}
